import React, { ReactElement } from 'react';
import { BreakdownSelection } from '../breakdownSelection';

interface LocationDetailBreadcrumbProps {
    selection: BreakdownSelection;
    /**
     * Which crumb describes what is rendered below it. The group only owns the panel while a
     * group-scoped tab is open; on the location-wide tabs, and while the location's details are
     * still loading, the location does.
     */
    current: 'location' | 'group';
    onBack?: () => void;
}

/**
 * The trail out of a breakdown drill-down. Lives apart from LocationDetailTile because it has
 * to render before the location's details arrive — the tile needs a `location` to render at
 * all, and without this the sidebar would show the "Select a location" placeholder mid-drill,
 * leaving no way back out until the request landed.
 *
 * Carries no stylesheet of its own: it is always rendered inside a `.location-detail-tile`,
 * which is where its rules live (LocationDetailTile.scss).
 */
const LocationDetailBreadcrumb = ({ selection, current, onBack }: LocationDetailBreadcrumbProps): ReactElement => (
    <div className='location-detail-tile-breadcrumb'>
        <button
            type='button'
            className='location-detail-tile-back'
            aria-label='Back to breakdown'
            title='Back to breakdown'
            onClick={onBack}>
            {/* fa-arrow-left is not in the bundled FA6 subset and renders as tofu, so this is
                fa-arrow-right turned around in CSS — the same trick the breakdown panel's open
                chevron uses. */}
            <i className='fa-solid fa-arrow-right' aria-hidden='true' />
        </button>
        {/* The trail stays put on every tab — it is how the user got here, and it carries the
            only way back — but `aria-current` follows the title onto whichever crumb actually
            describes the body below. */}
        <nav aria-label='Breadcrumb'>
            <ol className='location-detail-tile-crumbs'>
                <li aria-current={current === 'location' ? 'page' : undefined}>
                    {selection.locationName}
                </li>
                <li aria-current={current === 'group' ? 'page' : undefined}>
                    {selection.groupName}
                </li>
            </ol>
        </nav>
    </div>
);

export default LocationDetailBreadcrumb;
